// Firebase Auth loads lazily via firebaseAuthLazy.js only when AuthProvider mounts
// This saves ~90KB Firebase Auth iframe for better initial page load
import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './index.css';
import './styles/journey-loading.css';
import { AuthProvider } from './context/AuthContext.jsx';
import JourneyLoading from './components/JourneyLoading.jsx';

// Eager-loaded components for fast initial render
import App from './App.jsx';
import AppWithTerms from './AppWithTerms.jsx';
import SiteLayout from './layout/SiteLayout.jsx';
import WhoIsPayingEntry from './pages/whoispaying/WhoIsPayingEntry.jsx';
import WhoIsPayingRoom from './pages/whoispaying/WhoIsPayingRoom.jsx';
import About from './pages/About.jsx';
import Faq from './pages/Faq.jsx';
import Account from './pages/Account.jsx';
import AuthCallback from './pages/AuthCallback.jsx';
import PrivacyTerms from './pages/PrivacyTerms.jsx';
import EventTripPlanning from './pages/EventTripPlanning.jsx';
import InvitationWeddingTools from './pages/InvitationWeddingTools.jsx';
import CorporateEventSoftware from './pages/CorporateEventSoftware.jsx';
import FeedbackModal from './pages/Feedback.tsx';

// Lazy-load only the heaviest, least-used routes
const Journey = lazy(() => import('./pages/Journey.jsx'));
const Moments = lazy(() => import('./pages/Moments.jsx'));
const MyMoments = lazy(() => import('./pages/MyMoments.jsx'));
const InvitePreview = lazy(() => import('./pages/InvitePreview.jsx'));
const RafflePlaceholder = lazy(() => import('./pages/RafflePlaceholder.jsx'));
const AdminCenter = lazy(() => import('./pages/AdminCenter.jsx'));

// Theme initialization
(function () {
  const theme = localStorage.getItem('theme');
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  if (theme === 'dark' || (!theme && prefersDark)) {
    document.documentElement.classList.add('dark');
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.classList.remove('dark');
    document.documentElement.setAttribute('data-theme', 'light');
  }
})();

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <AuthProvider>
      <AppWithTerms>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<JourneyLoading stage="flying" />}>
            <Routes>
              {/* Routes with shared layout (GlobalNav + Footer) */}
              <Route element={<SiteLayout />}>
                <Route path="/" element={<App />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/privacy-terms" element={<PrivacyTerms />} />
                <Route path="/account" element={<Account />} />
                <Route path="/admin" element={<AdminCenter />} />
                <Route path="/feedback" element={<FeedbackModal isPage={true} />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/__/auth/action" element={<AuthCallback />} />
                <Route path="/event-trip-planning" element={<EventTripPlanning />} />
                <Route path="/event-planning-tools" element={<EventTripPlanning />} />
                <Route path="/invitation-wedding-tools" element={<InvitationWeddingTools />} />
                <Route path="/invitation-wedding-planning-services" element={<InvitationWeddingTools />} />
                <Route path="/corporate-event-software" element={<CorporateEventSoftware />} />
              </Route>
              {/* Routes without shared layout */}
              <Route path="/whoispaying" element={<WhoIsPayingEntry />} />
              <Route path="/whoispaying/room/:roomId" element={<WhoIsPayingRoom />} />
              <Route path="/raffle" element={<RafflePlaceholder />} />
              <Route path="/planner" element={<Moments />} />
              <Route path="/planner/profile" element={<MyMoments />} />
              <Route path="/planner/journey" element={<Journey />} />
              <Route path="/i/:slug/:code" element={<InvitePreview />} />
              <Route path="/i/:code" element={<InvitePreview />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AppWithTerms>
    </AuthProvider>
  </StrictMode>,
)
